import React, { FC, useEffect, useState } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { InvestmentRoundingSetting } from 'shared';
import { twMerge } from 'tailwind-merge';

import { CalendarIcon, InfoCircle } from '../../../../assets/icons/index';
import {
  AnimatedCalculatedInput,
  AnimatedNumber,
} from '../../../../components/animated-number/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { OverlayDatePicker as DatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../components/hover-card/index';
import { FormInput } from '../../../../components/input/index';
import { Switch } from '../../../../components/switch/index';
import { EventHooks, useFormat, useLocale } from '../../../../hooks/index';
import {
  EventTypeBackend,
  EventUnionWithPermissions,
  FundraisingEvent,
  SortByType,
} from '../../../../types/events.types';
import { PageRoute } from '../../../../types/page.types';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { EventCombobox, FundraisingRoundingSettingField } from '../components/index';
import { EditEventStepVariant } from '../event-form.types';
import { defaultSharePrice, FormSchema } from '../event-form.validation';

const isFundraisingRoundEvent = (
  event: EventUnionWithPermissions,
): event is EventUnionWithPermissions & FundraisingEvent =>
  event.type === EventTypeBackend.FUNDRAISING_ROUND;

export type FundraisingRoundStepOneProps = {
  companyId: string;
  control: Control<FormSchema>;
  event: EventUnionWithPermissions | null;
  handleCloseModal: () => void;
  issuedShares: number;
  lockMode: boolean;
  onEditClick?: (step: EditEventStepVariant) => void;
  setFormData: UseFormSetValue<FormSchema>;
};
export const FundraisingRoundStepOne: FC<FundraisingRoundStepOneProps> = ({
  control,
  setFormData,
  companyId,
  issuedShares,
  lockMode,
  handleCloseModal,
  onEditClick,
  event,
}) => {
  const navigate = useNavigate();
  const { messagesLocale } = useLocale();
  const { format } = useFormat();
  const { stepOne } = useWatch<FormSchema>({ control });

  const { events } = EventHooks.useEvents({
    companyId,
    take: 100,
    typesFilter: [SortByType.FUNDRAISING_ROUND],
  });

  const filteredFundraisingEvents = events.filter(isFundraisingRoundEvent);
  const selectedValuationId = stepOne?.eventDetails?.['fundraising-round']?.event?.id;

  const duplicatedEvent = filteredFundraisingEvents.find(
    (fundraisingEvent) => fundraisingEvent.valuation.id === selectedValuationId,
  );
  const showDuplicateRoundBanner = event
    ? !!event.canEdit && !!duplicatedEvent
    : !!duplicatedEvent?.canEdit;

  const handleOpenEditModal = () => {
    if (!duplicatedEvent?.eventId) {
      return;
    }

    const isCurrentRound =
      !!event && (event.eventId === duplicatedEvent.eventId || event.id === duplicatedEvent.id);

    if (isCurrentRound) {
      onEditClick?.(2);
      return;
    }

    navigate(`${PageRoute.EVENTS}?openEditModal=true&selectedEventId=${duplicatedEvent.eventId}`, {
      replace: true,
    });
    handleCloseModal();
  };

  const formEvent = stepOne?.eventDetails?.['fundraising-round']?.event;
  const preMoneyValuation =
    formEvent?.sharePrice &&
    formEvent?.sharePrice !== defaultSharePrice &&
    formEvent?.issuedSharesOnStart !== undefined
      ? formEvent?.sharePrice * formEvent?.issuedSharesOnStart
      : formEvent?.sharePrice && formEvent?.sharePrice !== defaultSharePrice
        ? formEvent?.sharePrice * issuedShares
        : 0;

  // API dates arrive as JSON strings despite the `Date` type — the calendar reads Date methods
  // off `fromDate`, so hand it a real Date.
  const transactionDate = event?.date ? new Date(event.date) : undefined;

  // const latestDate = optionalStep?.['fundraising-round']?.investmentItems?.reduce<Date | null>(
  //   (latest, item) => {
  //     const currentDate = item.investmentDate;
  //     if (!latest || (currentDate && currentDate > latest)) {
  //       return currentDate || latest;
  //     }
  //     return latest;
  //   },
  //   null,
  // );

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const fundraisingRoundDetails = stepOne?.eventDetails?.['fundraising-round'];
  const fundraisingRoundEnabled = fundraisingRoundDetails?.enabled;
  const fundraisingRoundIsOpen = fundraisingRoundDetails?.isOpen;
  const fundraisingRoundOpenDate = fundraisingRoundDetails?.date;
  const fundraisingRoundRoundingSetting = fundraisingRoundDetails?.roundingSetting;

  useEffect(() => {
    if (!isInitialLoad) {
      return;
    }

    if (fundraisingRoundIsOpen === undefined) {
      setFormData('stepOne.eventDetails.fundraising-round.isOpen', true);
    }
    if (fundraisingRoundEnabled && fundraisingRoundRoundingSetting === undefined) {
      setFormData(
        'stepOne.eventDetails.fundraising-round.roundingSetting',
        InvestmentRoundingSetting.DEFAULT,
      );
    }
    setIsInitialLoad(false);
  }, [
    fundraisingRoundEnabled,
    fundraisingRoundIsOpen,
    fundraisingRoundRoundingSetting,
    isInitialLoad,
    setFormData,
  ]);

  const [isSwitchChecked, setIsSwitchChecked] = useState(fundraisingRoundDetails?.isOpen ?? true);

  useEffect(() => {
    setIsSwitchChecked(fundraisingRoundDetails?.isOpen ?? true);
  }, [fundraisingRoundDetails?.isOpen]);

  // A round blocked by a later valuation keeps exactly one allowed edit: closing it. Both backend
  // gates honour that and share one predicate (isRestrictedFundraisingOpenToggleEdit) — the accept
  // gate EventsMutationChecksService.assertCanEdit and the apply strategy
  // FundraisingEventWorkflowDispatchStrategy.tryRestrictedPatchForEdit. Gating this switch on
  // `canEdit` alone contradicted that contract and left the round impossible to close. Re-opening a
  // closed round is a real edit and still requires `canEdit` — a round in a previous valuation
  // stays view-only once closed; on create (`event` is null) the switch is unrestricted, as before.
  const persistedRoundIsOpen = event && isFundraisingRoundEvent(event) ? event.isOpen : undefined;
  const isCloseOnlyRound = !!event && !event.canEdit && persistedRoundIsOpen === true;

  // The switch reflects the pending form value, not the persisted one, and the list's close toggle
  // seeds the form with `isOpen: false` before anything is saved. Keeping the switch live once it
  // reads closed let a blocked round be flipped back to open — which unlocked every field below and
  // offered an Update the accept gate rejects. On a close-only round the switch is therefore one-way:
  // usable while it still reads open, locked once closed (Cancel abandons the pending close).
  const canToggleRoundState =
    !event || !!event.canEdit || (isCloseOnlyRound && isSwitchChecked === true);

  // Same contract for the rest of the form: the restricted patch applies isOpen/closeDate only, so a
  // close-only round must not present its other fields as editable regardless of the switch state.
  const areRoundDetailsLocked = isCloseOnlyRound || (lockMode && !fundraisingRoundIsOpen);

  return (
    <div className="flex w-full flex-col gap-6">
      <div
        className={twMerge(
          'flex flex-col gap-3 rounded-md',
          isSwitchChecked
            ? 'border-[1px] border-gray-100 bg-gray-25 py-3'
            : 'bg-fireside-25 px-1 pb-1 pt-3',
        )}
      >
        <div className={twMerge('flex items-center gap-2', isSwitchChecked ? 'px-4' : 'px-3')}>
          <Switch
            checked={isSwitchChecked}
            disabled={event ? !canToggleRoundState : undefined}
            // The closing date is picked manually, never derived. Defaulting it to the opening
            // date (or today) put a date the user never chose onto a round being closed.
            onChange={(state) => {
              setIsSwitchChecked(state);
              setFormData('stepOne.eventDetails.fundraising-round.isOpen', state);
            }}
          />
          <div className="flex flex-col gap-1">
            <span
              className={twMerge(
                'text-sm font-[550]',
                isSwitchChecked ? 'text-forest-600' : 'text-fireside-600',
              )}
            >
              {isSwitchChecked ? (
                <AppFormattedMessage id="OPEN" />
              ) : (
                <AppFormattedMessage id="CLOSED" />
              )}
            </span>
            <span className="text-xs font-[450] text-gray-500">
              <AppFormattedMessage
                id="FUNDRAISING_ROUND_STATUS"
                values={{
                  state: isSwitchChecked ? messagesLocale['OPEN'] : messagesLocale['CLOSED'],
                }}
              />
            </span>
          </div>
        </div>
        {!isSwitchChecked && (
          <div className="rounded-md bg-white p-3 shadow-xs">
            <DatePicker
              calendar={{
                // A round cannot close before it opened; follow the form's opening date so the
                // bound tracks an edit to it.
                fromDate: fundraisingRoundOpenDate || transactionDate || undefined,
              }}
              defaultMonth={stepOne?.eventDetails?.['fundraising-round']?.date || undefined}
              onSelect={(date) =>
                setFormData(`stepOne.eventDetails.fundraising-round.closeDate`, date)
              }
              value={stepOne?.eventDetails?.['fundraising-round']?.closeDate}
              wrapperClassName="w-full"
            >
              <FormInput
                autoComplete="off"
                control={control}
                disabledInput
                icon={<CalendarIcon className="mr-2 size-6" iconColor="#98A2B3" />}
                name={`stepOne.eventDetails.fundraising-round.closeDate`}
                placeholder="Closing date"
                transform={(value) => {
                  return value ? format(value, 'dd/MM/yyyy') : '';
                }}
                wrapperClassName="w-full"
              />
            </DatePicker>
          </div>
        )}
      </div>

      <div className="flex w-full flex-col gap-4">
        <EventCombobox
          companyId={companyId}
          control={control}
          disabled={areRoundDetailsLocked}
          fieldName={`stepOne.eventDetails.fundraising-round.event`}
          lockMode={lockMode}
          onlyWithSharePrice
        />
        {/* Shown in both states: a closed round still has an opening date, and hiding this field
            while closed left its validation errors invisible. Gated on `isCloseOnlyRound` rather
            than `areRoundDetailsLocked` so a closed round in the current valuation keeps its
            opening date pickable, as before. */}
        <DatePicker
          disabled={isCloseOnlyRound}
          onSelect={(date) => setFormData(`stepOne.eventDetails.fundraising-round.date`, date)}
          value={stepOne?.eventDetails?.['fundraising-round']?.date}
          wrapperClassName="w-full"
        >
          <FormInput
            autoComplete="off"
            control={control}
            disabledInput
            icon={<CalendarIcon className="mr-2 size-6" iconColor="#98A2B3" />}
            name={`stepOne.eventDetails.fundraising-round.date`}
            placeholder={<AppFormattedMessage id="OPEN_DATE" />}
            transform={(value) => {
              return value ? format(value, 'dd/MM/yyyy') : '';
            }}
            wrapperClassName="w-full"
          />
        </DatePicker>

        {showDuplicateRoundBanner && (
          <span className="rounded bg-fireside-50 p-3 text-xs font-[450] text-gray-700">
            <AppFormattedMessage id="FUNDRAISING_ROUND_CONDITIONS" />
            <span
              className="cursor-pointer font-[550] text-brand-700 underline"
              onClick={handleOpenEditModal}
            >
              {' '}
              <AppFormattedMessage id="CLICK_HERE" />
            </span>
            .
          </span>
        )}

        <FormInput
          control={control}
          currencySign
          disabled={
            stepOne?.eventDetails?.['fundraising-round']?.event?.id !== 'new' ||
            areRoundDetailsLocked
          }
          maxDecimalPlaces={4}
          name={'stepOne.eventDetails.fundraising-round.event.sharePrice'}
          placeholder={<AppFormattedMessage id="SHARES_PRICE" />}
          shouldDisplayZero
          shouldFormatNumber
          transform={(value) => {
            return value && Number(value) !== defaultSharePrice
              ? toMathRound(value, 4).toString()
              : '';
          }}
          wrapperClassName="w-full"
        />

        {stepOne?.eventDetails?.['fundraising-round']?.event?.sharePrice &&
          Number(stepOne?.eventDetails?.['fundraising-round']?.event?.sharePrice) === 0 && (
            <span className="rounded bg-fireside-50 p-3 text-xs font-[450] text-gray-700">
              {messagesLocale['SHARE_PRICE_FOR_FUNDRAISING_EVENT_CANT_BE_ZERO']}
            </span>
          )}

        <FundraisingRoundingSettingField control={control} disabled={areRoundDetailsLocked} />
      </div>
      <div className="flex flex-col gap-3 rounded-md bg-brand-25 px-1 pb-1 pt-3">
        <div className="flex flex-col items-start gap-1 pl-[14px]">
          <div className="flex items-center gap-2">
            <span className="text-sm font-[550] text-brand-700">
              <AppFormattedMessage id="PRE_MONEY_VALUATION" />
            </span>
            <HoverCard>
              <HoverCardTrigger>
                <InfoCircle className="size-4 cursor-pointer" iconColor="#2565C8 " />
              </HoverCardTrigger>
              <HoverCardContent
                className="z-20 w-max rounded-lg bg-[#101828] px-3 pb-[6px] pt-1"
                isShownTriangle
                side="top"
                triangleClassName="bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 ml-0"
              >
                <span className="text-label-md font-[550] text-white">
                  <AppFormattedMessage id="CALCULATED_BY" />
                </span>
              </HoverCardContent>
            </HoverCard>
          </div>
          <span className="text-xs font-[450] text-gray-700">
            <AppFormattedMessage id="ISSUED_SHARES" />:{' '}
            <AnimatedNumber
              integer
              value={
                stepOne?.eventDetails?.['fundraising-round']?.event?.issuedSharesOnStart !==
                undefined
                  ? Number(stepOne?.eventDetails?.['fundraising-round']?.event?.issuedSharesOnStart)
                  : Number(issuedShares)
              }
            />
          </span>
        </div>
        <div className="flex flex-col gap-3 rounded-md bg-white p-3 shadow-xs">
          <AnimatedCalculatedInput
            currencySign
            placeholder={<AppFormattedMessage id="VALUE" />}
            value={preMoneyValuation}
            wrapperClassName="w-full"
          />
          <DropDown
            answer={messagesLocale['PRE_MONEY_VALUATION_EXPLANATION']}
            className="rounded-lg bg-gray-50"
            question={<AppFormattedMessage id="VALUE_CALCULATION_INFO" />}
          />
        </div>
      </div>
      <DropDown
        answer={[
          messagesLocale['FUNDRAISING_EVENT_EXPLANATION_1'],
          messagesLocale['FUNDRAISING_EVENT_EXPLANATION_2'],
          messagesLocale['FUNDRAISING_EVENT_EXPLANATION_3'],
        ]}
        className="rounded-lg bg-gray-50"
        question={<AppFormattedMessage id="WHAT_IS_FUNDRAISING_ROUND" />}
      />
    </div>
  );
};
